import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MessageSquare, Loader2, Send, Upload, Clock, CheckCircle, AlertCircle, Bot, X, FileIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "general",
    priority: "medium",
    attachments: []
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
    } catch (error) {
      toast.error("Error al cargar tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.description) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.post(`${API}/tickets`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Ticket creado. Nuestro equipo ya está trabajando en ello.");
      setDialogOpen(false);
      setFormData({ subject: "", description: "", category: "general", priority: "medium", attachments: [] });
      loadTickets();
      
      // Show AI response
      if (response.data.ai_response?.message) {
        setSelectedTicket(response.data.ticket);
        setTicketMessages([{
          id: "ai-initial",
          sender_type: "ai",
          sender_name: "Soporte CIA Factura℗",
          message: response.data.ai_response.message,
          created_at: new Date().toISOString()
        }]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const loadTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketMessages(response.data.messages);
    } catch (error) {
      toast.error("Error al cargar mensajes");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.post(
        `${API}/tickets/${selectedTicket.id}/messages`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTicketMessages(prev => [...prev, response.data.user_message, response.data.ai_response]);
      setNewMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFile(true);
    const token = localStorage.getItem("cia_token");

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await axios.post(`${API}/upload/ticket-attachment`, formDataUpload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        return { url: response.data.url, filename: file.name };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles.map(f => f.url)]
      }));
      
      toast.success(`${uploadedFiles.length} archivo(s) subido(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploadingFile(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleCloseTicket = async () => {
    if (!window.confirm("¿Confirmas que tu problema fue resuelto?")) return;

    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/tickets/${selectedTicket.id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ticket cerrado. ¡Gracias por tu retroalimentación!");
      setSelectedTicket(null);
      loadTickets();
    } catch (error) {
      toast.error("Error al cerrar ticket");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { color: "bg-yellow-100 text-yellow-700", label: "Abierto", icon: Clock },
      in_progress: { color: "bg-blue-100 text-blue-700", label: "En progreso", icon: Loader2 },
      ai_resolved: { color: "bg-green-100 text-green-700", label: "Resuelto por IA", icon: Bot },
      admin_required: { color: "bg-orange-100 text-orange-700", label: "Escalado", icon: AlertCircle },
      waiting_user: { color: "bg-purple-100 text-purple-700", label: "Esperando tu respuesta", icon: MessageSquare },
      closed: { color: "bg-slate-100 text-slate-700", label: "Cerrado", icon: CheckCircle }
    };
    const { color, label, icon: Icon } = config[status] || config.open;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">Soporte Técnico</h1>
          <p className="page-subtitle">Reporta problemas y recibe ayuda de nuestro equipo</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary w-full sm:w-auto" data-testid="new-ticket-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Reportar un Problema</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Asunto *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ej: Error al subir logo"
                  className="cia-input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="technical">Técnico</SelectItem>
                      <SelectItem value="billing">Facturación</SelectItem>
                      <SelectItem value="feature_request">Solicitud de función</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción del problema *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe el problema con el mayor detalle posible..."
                  rows={4}
                  className="cia-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Adjuntos (opcional)</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center relative">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploadingFile}
                  />
                  {uploadingFile ? (
                    <Loader2 className="w-8 h-8 text-[#5B9BD5] mx-auto mb-2 animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  )}
                  <p className="text-sm text-slate-500">
                    {uploadingFile ? "Subiendo..." : "Haz clic o arrastra archivos para adjuntar"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Screenshots ayudan a resolver más rápido (Max 5MB)</p>
                </div>
                {formData.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.attachments.map((url, index) => (
                      <div key={index} className="flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1 text-xs">
                        <FileIcon className="w-3 h-3" />
                        <span className="max-w-[150px] truncate">Archivo {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-slate-500 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="cia-btn-primary" disabled={submitting}>
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar Ticket"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Tickets list */}
        <div className="lg:col-span-1">
          <Card className="cia-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Mis Tickets</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {tickets.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No tienes tickets abiertos</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                  {tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      onClick={() => loadTicketDetails(ticket)}
                      className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                        selectedTicket?.id === ticket.id ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium text-sm text-slate-900 line-clamp-1">{ticket.subject}</p>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <p className="text-xs text-slate-500">{formatDate(ticket.created_at)}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ticket detail / Chat */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card className="cia-card h-full flex flex-col">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{selectedTicket.subject}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{selectedTicket.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedTicket.status)}
                    {selectedTicket.status !== "closed" && (
                      <Button size="sm" variant="outline" onClick={handleCloseTicket}>
                        Cerrar Ticket
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                  {ticketMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender_type === "user"
                            ? "bg-[#5B9BD5] text-white"
                            : msg.sender_type === "ai"
                            ? "bg-slate-100 text-slate-900"
                            : "bg-green-100 text-green-900"
                        }`}
                      >
                        <p className="text-xs font-medium mb-1 opacity-70">
                          {msg.sender_name}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-xs opacity-50 mt-1">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input */}
                {selectedTicket.status !== "closed" && (
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Escribe tu mensaje..."
                        className="flex-1"
                        onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      />
                      <Button onClick={handleSendMessage} disabled={sendingMessage} className="cia-btn-primary">
                        {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="cia-card h-full flex items-center justify-center">
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Selecciona un ticket para ver los detalles</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
